import {api} from "@/shared/api";
import * as Input from "@/shared/utils/input";

import {DialogMode, RecordDialog, RecordDialogConfig} from "@/shared/recording/recordDialog";
import {WorkingTimeInfoPanel} from "@/shared/workingtime/workingTimeInfoPanel";
import {DescriptionTypeahead} from "@/shared/ui/descriptionTypeahead";
import {ValidationsListDialogComponent} from "@/shared/validations/validationsListComponent";
import {SubstituteSelection} from "@/shared/workingtime/substituteSelection";
import {WorkingTimeTypeCreditabilityCompensationUtil} from "@/shared/workingtime/workingTimeTypeCreditabilityCompensationUtil";
import {statusElementsHolder} from "@/shared/recording/statusElementsHolder";
import {SubstituteValidation} from "@/shared/workingtime/substituteValidation";
import {FormUtil} from "@/shared/forms/formUtil";
import {
	isWorkingTimeDateTimeChangeAllowed,
	isWorkingTimeDeletable,
	isWorkingTimeTypeChangeAllowed,
	permissionForSaveAndApproveButton
} from "@/shared/securityHelper";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {mergeProperties, updateCutInfo, updateDeductibleInfo} from "@/shared/utils/misc";
import {DOMUtil} from "@/shared/utils/dom";
import {SecurityStatus} from "@/shared/securityStatus";
import {
	AbstractWorkingTimeRequestResponseModel,
	CalculateVacationResponse,
	CustomFieldDefinition,
	ErrorResponse,
	ReportWorkingTime,
	SaveWorkingTimeDateSpanResponse,
	SaveWorkingTimeResponse,
	User,
	WorkingTimeType
} from "@/shared/timr";
import {
	formatDate,
	formatDateTime,
	formatDurationDays,
	formatVacation,
	getCurrentISOOffset,
	getISODate,
	getISOZonedDateTime,
	isSameDay,
	normalizeTimeInput,
	parseDateInput,
	parseISODate,
	Temporal
} from "@/shared/utils/datefunctions";
import {
	DurationUnit,
	ProofRequirement,
	RecordedEntryStatus,
	WorkingTimeRequestStatus,
	WorkingTimeTypeCategory,
	WorkingTimeTypeRecordingMode
} from "@/shared/timrTypes";
import {SaveWorkingTimeRequestResponse, WorkingTimeRequestUtil} from "@/shared/workingtime/workingTimeRequestUtil";
import {ProofAttachmentGallery} from "@/shared/workingtime/proofAttachmentGallery";
import {mapWorkingTimeToReportWorkingTime} from "@/shared/utils/recordMapper";
import {removeCustomFieldValues} from "@/shared/customFieldHelper";
import {Notify} from "@/shared/ui/notify";

export interface WorkingTimeDialogConfig extends RecordDialogConfig {
	workingTimeTypes: WorkingTimeType[];
}

export class WorkingTimeDialog extends RecordDialog<ReportWorkingTime> {

	/**
	 * Fired when the user chooses to open the saved request after a proof-upload failure.
	 * The event carries the {@link AbstractWorkingTimeRequestResponseModel} to open.
	 */
	public static EVENT_OPEN_REQUEST = "openRequest";

	declare public config: WorkingTimeDialogConfig;

	declare private editUnit: DurationUnit;
	private hasWorkingTimeDateSpan: boolean;
	declare private updateVacationInformationRequest: JQuery.jqXHR;
	declare private workingTimeInfoPanel: WorkingTimeInfoPanel;
	declare private descriptionTypeahead: DescriptionTypeahead;
	private daysDateDifference = 0;
	declare private validationsList: ValidationsListDialogComponent;
	declare private substituteSelection?: SubstituteSelection;
	declare private substituteValidation?: SubstituteValidation;
	declare private proofAttachmentGallery: ProofAttachmentGallery;
	private currentRecording: ReportWorkingTime | null = null;

	private currentRequestCount = 0;

	constructor(config: WorkingTimeDialogConfig) {
		config = mergeProperties({
			title: {
				Create: "workingTime.header.add",
				Edit: "workingTime.header.edit",
				Show: "workingTime.header",
				Duplicate: "workingTime.header.duplicate"
			},
			users: [],
			workingTimeTypes: []
		}, config);
		super(config);
		this.editUnit = DurationUnit.Minutes;
	}

	protected initDialog() {
		const dialogButtons: BootstrapModal.ModalButton[] = [{
			text: "dialog.delete",
			action: () => {
				this._delete();
			},
			css: "deleteButton btn-outline-danger",
			left: true
		}, {
			text: "dialog.save",
			action: () => {
				this.save();
			},
			css: "saveButton btn-primary"
		}, {
			text: "dialog.save",
			action: () => {
				this.checkConfigurationAndSaveDateSpan(this.dialog);
			},
			visible: false,
			css: "saveDateSpanButton btn-primary"
		}, {
			text: "dialog.cancel",
			action: () => {
				this.closeDialog();
			},
			css: "cancelButton btn-default",
		}];

		if (SecurityStatus.isWorkingTimeRequestsEnabled()) {
			dialogButtons.unshift({
				text: "dialog.request",
				action: () => {
					this.checkConfigurationAndSaveRequest(this.dialog);
				},
				css: "requestButton btn-primary",
				visible: false,
				left: false
			});
			dialogButtons.unshift({
				text: "dialog.saveAndApprove",
				action: () => {
					this.checkConfigurationAndSaveRequest(this.dialog, true);
				},
				css: "saveAndApproveButton btn-primary",
				left: false,
				visible: false
			});
		}

		this.dialog = new BootstrapModal.RecordModal({
			size: this.config.size,
			title: this.config.title ? this.config.title[this.dialogMode] : undefined,
			message: this.dom,
			css: {
				content: "border-NOT_INITIALIZED"
			},
			buttons: dialogButtons,
			checkbox: true,
			onOpen: () => {
				this.updatePrimaryButtons(this.dialogMode === DialogMode.Show);
				this.updateBorder();
			},
			onClose: () => {
				this.proofAttachmentGallery?.reset();
				this.dialog.setCheckboxValue(false);
			}
		});

		this.workingTimeInfoPanel = new WorkingTimeInfoPanel($("#workingTimeDialogInfoPanel"));

		this.descriptionTypeahead = new DescriptionTypeahead({
			element: this.dom.find(".descriptionTypeahead"),
			url: "filterWorkingTimeDescriptions.json"
		});

		this.initWorkingTimeTypeSelect();
		this.initSubstitutes();

		this.dom.find(".endDate").on("focusout", () => {
			this.calculateDaysDateDifference();
		});

		this.dom.find(".endDate").on("apply.daterangepicker focusout", () => {
			if (this.editUnit !== DurationUnit.Minutes) {
				this.updateDuration();
			}
		});

		this.dom.find("#workingTimeDialogStartHalfDay, #workingTimeDialogEndHalfDay").change(() => {
			this.updateVacationInformation();
		});

		if (globals.savedValidationsEnabled) {
			this.validationsList = document.getElementById("workingTimeValidations") as ValidationsListDialogComponent;
		}

		this.proofAttachmentGallery = document.getElementById("workingTimeDialogProofAttachmentGallery") as ProofAttachmentGallery;
		this.proofAttachmentGallery?.init("requests/getProofAttachments.json");
	}

	private initSubstitutes() {
		this.substituteSelection = document.getElementById("workingTimeDialogSubstituteSelection") as SubstituteSelection;
		this.substituteValidation = document.getElementById("workingTimeDialogSubstituteValidation") as SubstituteValidation;
		this.substituteSelection?.init();
		this.substituteValidation?.init(false);

		this.initSubstituteListeners();
		this.initButtonLoadingListeners();
	}

	private initSubstituteListeners() {
		this.substituteSelection?.addEventListener(SubstituteSelection.EVENT_SUBSTITUTE_SELECTED, e => {
			const customEvent = e as CustomEvent<User>;
			this.substituteValidation?.validateSubstitute(customEvent.detail);
		});
		this.substituteSelection?.addEventListener(SubstituteSelection.EVENT_USER_SELECTED, e => {
			const customEvent = e as CustomEvent<number>;
			this.substituteValidation?.validateUser(customEvent.detail);
		});
		this.substituteSelection?.addEventListener(SubstituteSelection.EVENT_SUBSTITUTE_SELECTION_VISIBILITY_TOGGLED, e => {
			const customEvent = e as CustomEvent<boolean>;
			this.substituteValidation?.setRequiresSubstitute(customEvent.detail);
		});
	}

	private initButtonLoadingListeners() {
		this.substituteSelection?.addEventListener(SubstituteSelection.EVENT_START_LOADING, () => this.updateLoadingButtons(1));
		this.substituteValidation?.addEventListener(SubstituteValidation.EVENT_START_LOADING, () => this.updateLoadingButtons(1));
		this.substituteSelection?.addEventListener(SubstituteSelection.EVENT_FINISHED_LOADING, () => this.updateLoadingButtons(-1));
		this.substituteValidation?.addEventListener(SubstituteValidation.EVENT_FINISHED_LOADING, () => this.updateLoadingButtons(-1));
	}

	private updateLoadingButtons(modifyCount: number) {
		this.currentRequestCount += modifyCount;
		if (this.currentRequestCount > 0) {
			const buttons = this.dialog.getButtons("btn-primary");
			Button.setLoading(buttons);
		} else {
			this.currentRequestCount = 0;
			this.dialog.resetLoadingButtons();
			this.validateDateTimeChangeableUntil();
		}
	}

	public create(lastWorkingTime?: Partial<ReportWorkingTime>, createAnother?: boolean | Temporal.PlainDate, updateWorkingTimeTypes?: WorkingTimeType[]) {
		super.create(lastWorkingTime, createAnother !== false);
		super.initCustomFields();
		this.currentRecording = null;

		if (!SecurityStatus.isAddWorkingTimeAllowed()) {
			BootstrapModal.info({
				title: "account.expired.workingtime.title",
				message: "account.expired.workingtime",
				escapeText: false
			});
			return;
		}

		$("#workingTimeDialogStatus").val(this.config.defaultStatusNumber);
		$("#workingTimeDialogDescription").val("");
		$("#workingTimeDialogBreakTime").val("0");

		this.dom.find(".endHalfDay").prop("checked", false);

		if (!createAnother) {
			this.dom.find(".startHalfDay").prop("checked", false);
			this.initWorkingTimeTypeSelect();
			this.updatePrimaryButtons();
			if (updateWorkingTimeTypes) {
				this.updateWorkingTimeTypeSelect(updateWorkingTimeTypes);
			}
			if (lastWorkingTime?.workingTimeTypeId) {
				$("#workingTimeDialogWorkingTimeType").val(lastWorkingTime.workingTimeTypeId.toString());
				this.onSelectedWorkingTimeTypeChanged();
			}
		}

		/*
		 * if the previous record was a day based working time/request createAnother contains the default day
		 * this date will be the default startDate and endDate value for the next record
		 */
		if (createAnother instanceof Temporal.PlainDate) {
			const newDate = formatDate(createAnother);
			this.dom.find(".startDate").val(newDate);
			this.dom.find(".endDate").val(newDate);
			this.onSelectedWorkingTimeTypeChanged();
		}

		this.updateDurationFields();
		this.calculateDaysDateDifference();
		this.updateProofAttachmentGallery();

		updateDeductibleInfo(this.dom.find("[data-deductible-info]"));
		if (globals.savedValidationsEnabled) {
			updateCutInfo(this.dom.find("[data-cut-info]"), null);
			this.validationsList.validationsListItem = null;
		}

		this.showDialog();
	}

	protected showRecording(recording: ReportWorkingTime, duplicate = false) {
		this.currentRecording = recording;
		this.editUnit = recording.editUnit;
		this.hasWorkingTimeDateSpan = recording.workingTimeDateSpanId !== null;
		this.substituteSelection?.setSubstitute();

		super.showRecording(recording, duplicate);

		this.proofAttachmentGallery?.loadProofAttachments(null);

		this.dom.find(".startHalfDay").prop("checked", recording.durationDays === 0.5);
		this.dom.find(".endHalfDay").prop("checked", false);
		this.calculateDaysDateDifference();

		this.updateCustomFields(recording, this.config.customFieldDefinitions);
		updateDeductibleInfo(this.dom.find("[data-deductible-info]"), !duplicate ? recording : null);

		this.initWorkingTimeTypeSelect(recording.workingTimeTypeId);
		$("#workingTimeDialogDescription").val(recording.description);
		if (recording?.workingTimeTypeId) {
			$("#workingTimeDialogWorkingTimeType").val(recording.workingTimeTypeId.toString());

			// provide forceDateTimeChangeableAllowed depending on if mode is duplicate, to prevent showing the warning initially (should only visible after modification of date or working time type)
			this.onSelectedWorkingTimeTypeChanged(!duplicate);
		}

		if (duplicate && this.editUnit === DurationUnit.Minutes) {
			this.dom.find(".breakTime").val(recording.breakTimeManual);
		}

		// in duplicate dialog we do not show validations
		if (globals.savedValidationsEnabled) {
			updateCutInfo(this.dom.find("[data-cut-info]"), duplicate ? null : recording);
			this.validationsList.validationsListItem = duplicate ? null : {
				recordId: recording.id,
				validations: recording.validations
			};
		}

		this.updateDurationFields();
		this.updateProofAttachmentGallery();
	}

	private updateDurationFields() {
		// on break time we do not show "Pause" field
		// only show duration field named "Pause" if working time type is break time
		// TIMR-7619
		const workingTimeType = this.getSelectedWorkingTimeType();
		if (workingTimeType?.category === WorkingTimeTypeCategory.BreakTime) {
			this.dom.find(".breakTime").val(0);
			this.dom.find("#durationLabel").text(messages["workingTime.breakTime"]);
			this.updateBreakTimeFieldState(true);
			this.updateDuration();
		} else {
			this.dom.find("#durationLabel").text(messages["record.duration"]);
			this.updateBreakTimeFieldState();
		}
	}

	protected calculateDuration(start: Temporal.ZonedDateTime, end: Temporal.ZonedDateTime, breakTime: number): Temporal.Duration {
		let duration = super.calculateDuration(start, end, breakTime);
		const workingTimeType = this.getSelectedWorkingTimeType();
		if (workingTimeType) {
			const nonCreditableDeductible = WorkingTimeTypeCreditabilityCompensationUtil.getNonCreditableDeductible(workingTimeType);
			if (nonCreditableDeductible) {
				duration = duration.subtract({milliseconds: nonCreditableDeductible});
				if (duration.sign < 0) {
					duration = new Temporal.Duration();
				}
			}
		}
		return duration;
	}

	protected calculateStartDateTime(endDateTime: Temporal.ZonedDateTime, duration: Temporal.Duration, startTimeZone: string, breakTime: number): Temporal.ZonedDateTime {
		const startDateTime = super.calculateStartDateTime(endDateTime, duration, startTimeZone, breakTime);
		const workingTimeType = this.getSelectedWorkingTimeType();
		if (workingTimeType) {
			const nonCreditableDeductible = WorkingTimeTypeCreditabilityCompensationUtil.getNonCreditableDeductible(workingTimeType);
			if (nonCreditableDeductible) {
				return startDateTime.subtract({milliseconds: nonCreditableDeductible});
			}
		}
		return startDateTime;
	}

	protected calculateEndDateTime(startDateTime: Temporal.ZonedDateTime, duration: Temporal.Duration, endTimeZone: string, breakTime: number): Temporal.ZonedDateTime {
		const endDateTime = super.calculateEndDateTime(startDateTime, duration, endTimeZone, breakTime);
		const workingTimeType = this.getSelectedWorkingTimeType();
		if (workingTimeType) {
			const nonCreditableDeductible = WorkingTimeTypeCreditabilityCompensationUtil.getNonCreditableDeductible(workingTimeType);
			if (nonCreditableDeductible) {
				return endDateTime.add({milliseconds: nonCreditableDeductible});
			}
		}
		return endDateTime;
	}

	protected _delete() {
		const workingTimeId = $(this.config.dialogId).find(".id").val();
		if (!workingTimeId) {
			return;
		}
		const description = $(this.config.dialogId).find("#workingTimeDialogDescription").val() as string;
		const workingTimeType = this.getSelectedWorkingTimeType() ? this.getSelectedWorkingTimeType().name : "";
		const date = this.editUnit === DurationUnit.Minutes ? formatDateTime(this.parseStartDateTime()) : formatDate(this.parseStartDateTime());
		BootstrapModal.confirm({
			title: "workingtime.delete.title",
			message: messages.withArguments(
				"workingtime.delete.message",
				workingTimeType,
				description ? " " + messages.withArguments("record.delete.message.description", description) : "",
				date
			),
			yesLabel: "confirmation.delete",
			noLabel: "confirmation.cancel",
			yesAction: (modal: BootstrapModal.Modal) => {
				api("recording/deleteWorkingTime.json")
					.post({id: workingTimeId})
					.done(() => {
						modal.close();
						this.deleteDone();
					});
			},
			noAction: (modal) => {
				modal.close();
				this.deleteCancelled();
			}
		});
	}

	protected shouldShowTimeZoneFields(): boolean {
		return this.editUnit === DurationUnit.Minutes && super.shouldShowTimeZoneFields();
	}

	/**
	 * Validates the form before submitting. Returns false (and shows feedback) when the entry must not be sent to
	 * the backend, so all save paths can abort.
	 */
	private checkBeforeSubmit(): boolean {
		normalizeTimeInput($("#workingTimeDialogStartTime"));
		normalizeTimeInput($("#workingTimeDialogEndTime"));
		$("#workingTimeDialogWorkingTimeType").focus();
		let startDateTime: Temporal.ZonedDateTime | Temporal.PlainDate | null;
		let endDateTime: Temporal.ZonedDateTime | Temporal.PlainDate | null;
		if (this.editUnit === DurationUnit.Minutes) {
			startDateTime = this.parseStartDateTime();
			endDateTime = this.parseEndDateTime();
		} else {
			startDateTime = parseDateInput($("#workingTimeDialogStartDate").val() as string);
			endDateTime = parseDateInput($("#workingTimeDialogEndDate").val() as string);
		}
		const workingTimeType = this.getSelectedWorkingTimeType();
		if (workingTimeType && (workingTimeType.category !== WorkingTimeTypeCategory.AttendanceTime ||
			workingTimeType.editUnit !== DurationUnit.Minutes &&
			workingTimeType.recordingModeUser === WorkingTimeTypeRecordingMode.BY_REQUEST &&
			SecurityStatus.isWorkingTimeRequestsEnabled())) {
			removeCustomFieldValues(this.dom);
		}
		if (startDateTime == null) {
			BootstrapModal.error({message: "invalid.start.datetime"});
			return false;
		}
		const workingTimeDialogStartTimeZone = $("#workingTimeDialogStartTimeZone").val() as string;
		if (!globals.timeZonePattern.test(workingTimeDialogStartTimeZone)) {
			BootstrapModal.error({message: "invalid.start.timezone"});
			return false;
		}
		const workingTimeDialogStartDateTime = $("#workingTimeDialogStartDateTime");
		// The legacy day-based save path (Edit without a date span) posts this hidden field to
		// saveWorkingTime.json, where it binds via ZonedDateTimeEditor (strict ISO_OFFSET_DATE_TIME).
		// A day-only value would be rejected, so emit a full offset datetime at UTC midnight.
		workingTimeDialogStartDateTime.val(startDateTime instanceof Temporal.ZonedDateTime
			? getISOZonedDateTime(startDateTime)
			: getISOZonedDateTime(startDateTime.toZonedDateTime("+00:00"))
		);
		if (endDateTime == null) {
			BootstrapModal.error({message: "invalid.end.datetime"});
			return false;
		}
		const workingTimeDialogEndTimeZone = $("#workingTimeDialogEndTimeZone").val() as string;
		if (!globals.timeZonePattern.test(workingTimeDialogEndTimeZone)) {
			BootstrapModal.error({message: "invalid.end.timezone"});
			return false;
		}
		const workingTimeDialogEndDateTime = $("#workingTimeDialogEndDateTime");
		workingTimeDialogEndDateTime.val(endDateTime instanceof Temporal.ZonedDateTime
			? getISOZonedDateTime(endDateTime)
			: getISOZonedDateTime(endDateTime.toZonedDateTime("+00:00"))
		);
		if (!this.validateStartBeforeEnd(startDateTime, endDateTime)) {
			return false;
		}
		const workingTimeDialogBreakTime = $("#workingTimeDialogBreakTime");
		workingTimeDialogBreakTime.val((workingTimeDialogBreakTime.val() as string).replace(/\D/g, ""));
		if ($("#workingTimeDialogUserFilter").is(":visible") && !$("#workingTimeDialogUserId").val()) {
			BootstrapModal.error({message: "saveRecord.user.required"});
			return false;
		}
		this.dom.find("#workingTimeDialogStartHalfDayValue").val(String(this.dom.find(".startHalfDay").prop("checked")));
		this.dom.find("#workingTimeDialogEndHalfDayValue").val(String(this.dom.find(".endHalfDay").prop("checked")));
		return true;
	}

	/**
	 * Validates that the end is not before the start. The error is shown inline on the end date field with the same
	 * message the backend uses, instead of opening an error dialog. TIMR-12188
	 */
	private validateStartBeforeEnd(startDateTime: Temporal.ZonedDateTime | Temporal.PlainDate, endDateTime: Temporal.ZonedDateTime | Temporal.PlainDate): boolean {
		const $workingTimeDialogEndDate = $("#workingTimeDialogEndDate");
		Input.removeFeedback($workingTimeDialogEndDate);
		if ((startDateTime instanceof Temporal.ZonedDateTime && endDateTime instanceof Temporal.ZonedDateTime
			? Temporal.ZonedDateTime.compare(startDateTime, endDateTime) > 0
			: Temporal.PlainDate.compare(startDateTime, endDateTime) > 0)) {
			Input.addFeedback($workingTimeDialogEndDate, "has-error", messages["saveRecord.endBeforeStart"]);
			return false;
		}
		return true;
	}

	private checkConfigurationAndSaveDateSpan(modal: BootstrapModal.Modal): void {
		if (!this.checkBeforeSubmit()) {
			modal.resetLoadingButtons();
			return;
		}
		if (this.substituteValidation?.hasAlreadySubstitutions()) {
			BootstrapModal.confirm({
				title: "workingTimeDateSpans.dialog.alreadySubstitute.title",
				message: messages.withArguments("workingTimeDateSpans.dialog.alreadySubstitute.info", this.substituteValidation?.getAlreadySubstitutionsUserNames()),
				escapeText: false,
				preventDoubleClickButtons: true,
				yesAction: (modal: BootstrapModal.Modal) => {
					this.saveDateSpan();
					modal.close();
				},
				noAction: (m: BootstrapModal.Modal) => {
					m.close();
					modal.resetLoadingButtons();
				}
			});
		} else {
			this.saveDateSpan();
		}
	}

	protected saveDateSpan() {
		const workingTimeDialogForm = $("#workingTimeDialogForm");

		const startDate = parseDateInput(this.dom.find(".startDate").val() as string);
		const endDate = parseDateInput(this.dom.find(".endDate").val() as string);

		api("workingTimeDateSpans/saveWorkingTimeDateSpan.json")
			.options({contentType: "application/json"})
			.post(JSON.stringify({
				workingTimeType: {id: this.getSelectedWorkingTimeType().id},
				user: this.getSelectedUserId() ? {id: this.getSelectedUserId()} : null,
				start: getISODate(startDate),
				end: getISODate(endDate),
				description: this.dom.find("#workingTimeDialogDescription").val(),
				startHalfDay: this.dom.find(".startHalfDay").prop("checked") as boolean,
				endHalfDay: this.dom.find(".endHalfDay").prop("checked") as boolean,
				substitute: this.substituteSelection?.getSelectedUser()?.id ? {id: this.substituteSelection.getSelectedUser().id} : null
			}))
			.done(({durationDays, lastWorkingTime, nextAvailableDay}: SaveWorkingTimeDateSpanResponse) => {
				const selectedWorkingTimeType = workingTimeDialogForm.find("#workingTimeDialogWorkingTimeType option:selected").text();
				if (durationDays === 0) {
					BootstrapModal.warn({
						title: "saveWorkingTime.days.title",
						message: "saveWorkingTime.days.zero",
						escapeText: false
					});
					return;
				} else if (durationDays === 0.5) {
					BootstrapModal.info({
						title: "saveWorkingTime.days.title",
						message: messages.withArguments("saveWorkingTime.days.half", formatDate(startDate), selectedWorkingTimeType)
					});
				} else if (durationDays === 1) {
					BootstrapModal.info({
						title: "saveWorkingTime.days.title",
						message: messages.withArguments("saveWorkingTime.days.one", formatDate(startDate), selectedWorkingTimeType)
					});
				} else {
					BootstrapModal.info({
						title: "saveWorkingTime.days.title",
						message: messages.withArguments("saveWorkingTime.days.many", formatDurationDays(durationDays), selectedWorkingTimeType)
					});
				}
				if (this.dialogMode === DialogMode.Create && this.dialog.getCheckboxValue()) {
					this.saveRecordDone(mapWorkingTimeToReportWorkingTime(lastWorkingTime), nextAvailableDay ? parseISODate(nextAvailableDay) : true);
				} else {
					this.saveRecordDone();
				}
			})
			.fail(() => {
				this.saveFailed();
			});
	}

	protected save() {
		if (!this.checkBeforeSubmit()) {
			this.dialog.resetLoadingButtons();
			return;
		}
		const workingTimeDialogForm = $("#workingTimeDialogForm");

		api("recording/saveWorkingTime.json")
			.postForm(workingTimeDialogForm, {
				serializeDisabled: true
			})
			.done((data: SaveWorkingTimeResponse) => {
				if (this.dialogMode === DialogMode.Create && this.dialog.getCheckboxValue()) {
					this.saveRecordDone(mapWorkingTimeToReportWorkingTime(data.workingTimes[0]), true);
				} else {
					this.saveRecordDone();
				}
			})
			.fail(() => {
				this.saveFailed();
			});
	}

	protected saveRecordDone(lastWorkingTime?: ReportWorkingTime, createAnother?: Temporal.PlainDate | boolean) {
		if (this.dialogMode === DialogMode.Create) {
			Notify.success(messages["recording.added"]);
			this.onRecordCreated();
			if (createAnother) {
				this.create(lastWorkingTime, createAnother);
				return;
			}
		} else {
			this.onRecordEdited();
		}
		this.closeDialog();
	}

	private checkConfigurationAndSaveRequest(modal: BootstrapModal.Modal, approveImmediately: boolean = false): void {
		if (this.substituteValidation?.hasAlreadySubstitutions()) {
			BootstrapModal.confirm({
				title: "workingTimeRequestDialog.alreadySubstitute.title",
				message: messages.withArguments("workingTimeRequestDialog.alreadySubstitute.info", this.substituteValidation?.getAlreadySubstitutionsUserNames()),
				escapeText: false,
				preventDoubleClickButtons: true,
				yesAction: (modal: BootstrapModal.Modal) => {
					this.saveRequest(approveImmediately);
					modal.close();
				},
				noAction: (m: BootstrapModal.Modal) => {
					m.close();
					modal.resetLoadingButtons();
				}
			});
		} else {
			this.saveRequest(approveImmediately);
		}
	}

	private saveRequest(approveImmediately = false) {
		if (!this.checkBeforeSubmit()) {
			this.dialog.resetLoadingButtons();
			return;
		}
		const params = $("#workingTimeDialogForm").serializeArray();
		if (approveImmediately) {
			params.push({name: "status", value: WorkingTimeRequestStatus.Approved});
		}
		// The subtype is known from the selected working time type's edit unit, so we post to the matching endpoint.
		const saveWorkingTimeRequest = this.getSelectedWorkingTimeType().editUnit === DurationUnit.Minutes
			? WorkingTimeRequestUtil.saveWorkingTimeRequestWithProofAttachments
			: WorkingTimeRequestUtil.saveWorkingTimeDateSpanRequestWithProofAttachments;
		saveWorkingTimeRequest(params, this.proofAttachmentGallery, (workingTimeRequest) => this.onOpenRequest(workingTimeRequest))
			.then((data: SaveWorkingTimeRequestResponse) => {
				if (this.dialogMode === DialogMode.Create && this.dialog.getCheckboxValue()) {
					/*
					 * after saving we get a date nextAvailableDay from the server,
					 * we put this as the default startDate and endDate value from the next request after saving (createAnother)
					 */
					this.saveRequestDone(approveImmediately, data.workingTimeRequest, data.nextAvailableDay ? parseISODate(data.nextAvailableDay) : true);
				} else {
					this.saveRequestDone(approveImmediately);
				}
				statusElementsHolder.updateRequestsApproval();
			})
			.catch((error: ErrorResponse) => {
				// the error dialog is already surfaced by api(); here we only mark the field-level errors
				// (e.g. mandatory custom fields) so the invalid inputs on the form are highlighted
				if (error?.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors($("#workingTimeDialogForm"), error.responseJSON.fieldErrors);
				}
				this.saveFailed();
			})
			.finally(() => this.dialog.resetLoadingButtons());
	}

	protected saveRequestDone(approveImmediately: boolean, lastWorkingTimeRequest?: AbstractWorkingTimeRequestResponseModel, createAnother?: Temporal.PlainDate | boolean) {
		if (this.dialogMode === DialogMode.Create) {
			Notify.success(approveImmediately ? messages["requests.addedAndApproved"] : messages["requests.added"]);
			this.onRequestCreated();
			if (approveImmediately) {
				this.onRecordCreated();
			}
			if (lastWorkingTimeRequest) {
				this.create({
					userId: lastWorkingTimeRequest.user?.id,
					end: lastWorkingTimeRequest.end,
					workingTimeTypeId: lastWorkingTimeRequest.workingTimeType?.id
				}, createAnother);
				return;
			}
		} else {
			this.onRecordEdited();
		}
		this.closeDialog();
	}

	private onRequestCreated() {
		$(this).triggerHandler(RecordDialog.EVENT_REQUEST_CREATED);
	}

	private onOpenRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		$(this).triggerHandler(WorkingTimeDialog.EVENT_OPEN_REQUEST, [workingTimeRequest]);
	}

	protected onSelectedUserChanged(user: User) {
		super.onSelectedUserChanged(user);

		this.updatePrimaryButtons();
		this.updateVacationInformation();
		this.substituteSelection?.setUser(user?.id);
	}

	protected updateDuration() {
		const startDateString = this.dom.find(".startDate").val() as string;
		const endDateString = this.dom.find(".endDate").val() as string;
		if (this.editUnit === DurationUnit.Minutes) {
			super.updateDuration();
		} else {
			const startDate = parseDateInput(startDateString);
			const endDate = parseDateInput(endDateString);
			const sameDay = isSameDay(startDate, endDate);
			this.dom.find(".startHalfDayLabel").text(sameDay ? messages["workingTime.halfDay"] : messages["workingTime.startHalfDay"]);
			const endHalfDayInput = this.dom.find(".endHalfDay");
			if (sameDay) {
				endHalfDayInput.prop("checked", false);
			}
			endHalfDayInput.closest("label").toggle(!sameDay);
		}
		this.updateVacationInformation();
		this.updateSubstituteValidationStartEndDate();
	}

	protected updateVacationInformation() {
		this.dom.find("#workingTimeDialogVacationRequired").text("");
		this.dom.find("#workingTimeDialogVacationLeft").text("");
		this.dom.find(".vacationUnitLabel").text("");
		this.dom.find("#workingTimeDialogVacationWarning").text("").hide();
		this.updateVacationInformationRequest?.abort();
		const workingTimeType = this.getSelectedWorkingTimeType();
		const startDate = parseDateInput(this.dom.find(".startDate").val() as string);
		const endDate = parseDateInput(this.dom.find(".endDate").val() as string);
		const startHalfDay = this.dom.find(".startHalfDay").prop("checked") as boolean;
		const endHalfDay = this.dom.find(".endHalfDay").prop("checked") as boolean;
		if (this.dialogMode === DialogMode.Create && SecurityStatus.isTimeAccountAllowed() &&
			workingTimeType && workingTimeType.category === WorkingTimeTypeCategory.Vacation && startDate && endDate) {
			this.dom.find(".workingTimeTypeCategory-vacation .loadingIndicator").show();
			this.updateVacationInformationRequest = api("recording/calculateVacation.json")
				.get({
					userId: this.getSelectedUserId() ?? globals.userId,
					startDate: getISODate(startDate),
					endDate: getISODate(endDate),
					startHalfDay: startHalfDay,
					endHalfDay: endHalfDay,
					timeZone: getCurrentISOOffset()
				})
				.done((data: CalculateVacationResponse) => {
					FormUtil.removeAllFeedback($("#workingTimeDialogForm"));
					this.dom.find("#workingTimeDialogVacationRequired").text(formatVacation(data.vacationRequired, data.vacationUnit));
					this.dom.find(".vacationUnitLabel").text(messages["vacationUnit." + data.vacationUnit]);
					this.dom.find(".workingTimeTypeCategory-vacation .loadingIndicator").hide();
					this.dom.find("#workingTimeDialogVacationWarning").text(data.warningText).toggle(!!data.warningText);
					if (data.vacationAccount) {
						this.dom.find("#workingTimeDialogVacationLeft").text(formatVacation(data.vacationLeft, data.vacationUnit));
					}
					this.dom.find("#workingTimeDialogVacationLeftContainer").toggle(data.vacationAccount);
					this.dom.find("#vacationAccountDisabledInfo").toggle(!data.vacationAccount);
				})
				.fail((response: ErrorResponse, textStatus: string) => {
					if (textStatus === "abort") {
						return;
					}
					this.dom.find("#workingTimeDialogVacationRequired").text("?");
					this.dom.find(".workingTimeTypeCategory-vacation .loadingIndicator").hide();
					this.dom.find("#workingTimeDialogVacationWarning").hide();
					this.dom.find("#workingTimeDialogVacationLeftContainer").hide();
					this.dom.find("#vacationAccountDisabledInfo").hide();
					if (response.responseJSON?.fieldErrors) {
						FormUtil.markFieldErrors($("#workingTimeDialogForm"), response.responseJSON.fieldErrors);
					}
				});
		} else {
			this.dom.find(".workingTimeTypeCategory-vacation .loadingIndicator").hide();
		}
	}

	protected updateStartDateTime() {
		if (this.editUnit === DurationUnit.Minutes) {
			super.updateStartDateTime();
		}
		this.updateVacationInformation();
		this.updateSubstituteValidationStartEndDate();
	}

	private calculateDaysDateDifference() {
		const dialog = $(this.config.dialogId);
		const startDate = parseDateInput(dialog.find(".startDate").val() as string);
		const endDate = parseDateInput(dialog.find(".endDate").val() as string);
		this.daysDateDifference = startDate && endDate ? startDate.until(endDate).total('days') : 0;
	}

	protected updateEndDateTime() {
		if (this.editUnit === DurationUnit.Minutes) {
			super.updateEndDateTime();
		} else {
			const dialog = $(this.config.dialogId);
			const startDate = parseDateInput(dialog.find(".startDate").val() as string);
			if (startDate != null) {
				const endDate = this.daysDateDifference > 0 ? startDate.add({days: this.daysDateDifference}) : startDate;
				dialog.find(".endDate").val(formatDate(endDate));
				// Refresh the end offset from the end date itself; the end time fields are empty in day mode,
				// so parseEndDateTime() would be null and skip the refresh.
				this.updateTimeZoneField("end", this.asLocalDateTime(endDate.toZonedDateTime(Temporal.Now.timeZoneId())));
			}
		}
		this.updateVacationInformation();
		this.updateSubstituteValidationStartEndDate();
	}

	protected updateTitle() {
		const workingTimeType = this.getSelectedWorkingTimeType();
		if (this.dialogMode !== DialogMode.Create || !workingTimeType) {
			super.updateTitle();
		} else if (workingTimeType.category === WorkingTimeTypeCategory.AttendanceTime) {
			if (SecurityStatus.isWorkingTimeRequestsEnabled()) {
				this.dialog.setTitle(messages["workingTime.header.requestAttendanceTime"]);
			} else {
				this.dialog.setTitle(messages["workingTime.header.addAttendanceTime"]);
			}
		} else {
			if (SecurityStatus.isWorkingTimeRequestsEnabled()) {
				this.dialog.setTitle(messages["workingTime.header.requestAbsence"]);
			} else {
				this.dialog.setTitle(messages["workingTime.header.addAbsence"]);
			}
		}
	}

	protected updateUserCombobox(users: User[], selectedUserId: number = null) {
		super.updateUserCombobox(users, selectedUserId);
		if (!this.getSelectedUserId()) {
			const userId = selectedUserId || globals.userId;
			this.substituteSelection?.setUser(userId);
		}
	}

	private initWorkingTimeTypeSelect(workingTimeTypeId?: number) {
		const workingTimeTypeSelect = $("#workingTimeDialogWorkingTimeType");
		const old = workingTimeTypeSelect.val() as string;
		workingTimeTypeSelect.empty();

		const currentWorkingTimeType = workingTimeTypeId ? this.getWorkingTimeTypeById(workingTimeTypeId) : null;
		DOMUtil.fillWorkingTimeTypeSelect(workingTimeTypeSelect, this.config.workingTimeTypes.filter(wtt => {
			return !currentWorkingTimeType && !wtt.archived ||
				currentWorkingTimeType && isWorkingTimeTypeChangeAllowed(currentWorkingTimeType, wtt, this.isDateTimeChangeAllowed());
		}), false);

		if (old != null && !workingTimeTypeId) {
			workingTimeTypeSelect.val(old);
			this.onSelectedWorkingTimeTypeChanged();
		}

		workingTimeTypeSelect.unbind("change");
		workingTimeTypeSelect.on("change", () => {
			this.onSelectedWorkingTimeTypeChanged();
		});
		if (this.getSelectedWorkingTimeType() === null) {
			this.onSelectedWorkingTimeTypeChanged();
		}
	}

	/**
	 * Updates the working time type select with the given working time types.
	 * @param forceDateTimeChangeableAllowed If true, the date time changeable allowed will be forced to true.
	 * 										If a working time is edited/showed the warning should not be visible initially, only after the user changes the working time type/date
	 * @private
	 */
	private onSelectedWorkingTimeTypeChanged(forceDateTimeChangeableAllowed: boolean = false) {
		// "end before start feedback" must be removed on changing wtt (TIMR-7327)
		Input.removeFeedback($("#workingTimeDialogEndDate"));

		const workingTimeType = this.getSelectedWorkingTimeType();
		const dialog = $(this.config.dialogId);
		if (this.dialogMode === DialogMode.Create) {
			this.editUnit = workingTimeType != null ? workingTimeType.editUnit : DurationUnit.Minutes;
		}
		this.configureDateTimeFieldsForUser();
		if (this.editUnit !== DurationUnit.HalfDays) {
			dialog.find(".endHalfDay").prop("checked", false);
			dialog.find(".startHalfDay").prop("checked", false);
		}
		dialog.find(".durationUnit-days").toggle(this.editUnit === DurationUnit.Days || this.editUnit === DurationUnit.HalfDays);
		dialog.find(".durationUnit-halfDays").toggle(this.editUnit === DurationUnit.HalfDays);
		// We suspect that Safari has a bug: if the parent is not displayed at the time,
		// it does not correctly determine the proper display value.
		// Instead of simply removing the display property (as every other browser does),
		// Safari and/or jQuery end up setting display: block.
		// Therefore, we explicitly set the display property to "" if the unit is not selected.
		dialog.find(".durationUnit-minutes").css("display", this.editUnit === DurationUnit.Minutes ? "" : "none");
		dialog.find(".dateTimeGroup")
			.toggleClass("col-md-6", this.editUnit === DurationUnit.HalfDays)
			.toggleClass("col-md-12", this.editUnit !== DurationUnit.HalfDays);
		const startDate = dialog.find(".startDate");
		const endDate = dialog.find(".endDate");
		if (this.editUnit === DurationUnit.Minutes) {
			startDate.toggleClass("border-radius-right", false);
			endDate.toggleClass("border-radius-right", false);
			this.showOrHideTimeZoneFields();
			this.durationChanged = false;
		} else {
			startDate.toggleClass("border-radius-right", true);
			endDate.toggleClass("border-radius-right", true);
			this.durationChanged = true;
		}
		dialog.find(".workingTimeTypeCategory-vacation")
			.toggle(this.dialogMode === DialogMode.Create && SecurityStatus.isTimeAccountAllowed() &&
				workingTimeType && workingTimeType.category === WorkingTimeTypeCategory.Vacation);
		dialog.find("#workingTimeDialogCustomFields")
			.toggle(workingTimeType && workingTimeType.category === WorkingTimeTypeCategory.AttendanceTime &&
				workingTimeType.editUnit === DurationUnit.Minutes);
		this.updateDuration();
		this.workingTimeInfoPanel.update(workingTimeType, this.dialogMode === DialogMode.Create || this.dialogMode === DialogMode.Duplicate);
		this.descriptionTypeahead.setFilter(workingTimeType ? {workingTimeTypeId: workingTimeType.id} : {});

		this.updatePrimaryButtons();
		this.updateBorder();
		this.updateTitle();
		this.validateDateTimeChangeableUntil(forceDateTimeChangeableAllowed);
		this.updateDurationFields();
		this.substituteSelection?.setWorkingTimeType(workingTimeType);
		this.substituteValidation?.setWorkingTimeType(workingTimeType);
		this.updateProofAttachmentGallery();
	}

	private updatePrimaryButtons(forceHidePrimaryButton = false) {
		const showRequestButton = this.isRequestCreation();

		// DEVNOTE: we also need to support saving (half)-day based WorkingTimes *WITHOUT* a WorkingTimeDateSpan to support older clients
		const showSaveWorkingTimeButton = this.editUnit === DurationUnit.Minutes || this.dialogMode === DialogMode.Edit && !this.hasWorkingTimeDateSpan;

		const showSaveDateSpanButton = this.editUnit !== DurationUnit.Minutes && !showSaveWorkingTimeButton;

		this.dialog.getButtons("saveButton").toggle(!showRequestButton && !forceHidePrimaryButton && showSaveWorkingTimeButton);
		this.dialog.getButtons("saveDateSpanButton").toggle(!showRequestButton && !forceHidePrimaryButton && showSaveDateSpanButton);
		this.dialog.getButtons("requestButton").toggle(showRequestButton && !forceHidePrimaryButton);

		const saveAndApproveButton = this.dialog.getButtons("saveAndApproveButton");
		if (saveAndApproveButton) {
			let showSaveAndApproveButton = showRequestButton && !forceHidePrimaryButton;
			if (showSaveAndApproveButton) {
				showSaveAndApproveButton = permissionForSaveAndApproveButton(this.config.userSelectionEnabled, this.getSelectedUserId(),
					this.config.isGroupLeaderForThemself);
			}
			saveAndApproveButton.toggle(showSaveAndApproveButton);
		}
	}

	/**
	 * Checks whether the criteria for the date of an entry are met and whether the entry may be saved or a warning is displayed.
	 * Currently, there are only validations DateTimeChangeableType if the configuration is BY_USER_DAY_RANGE
	 * Keep in mind that this is only for minute-based entries
	 */
	protected validateDateTimeChangeableUntil(forceChangeableAllowed: boolean = false) {
		if (this.config.dateTimeChangeableByUserDayRangeUntil) {
			// The change is always allowed (no warning) for a day-based entry, and for a request — a request's
			// period is governed by request rules, not the by-user-day-range record restriction.
			this.updateDateTimeChangeableWarning(forceChangeableAllowed || this.isRequestCreation() ||
				this.getSelectedWorkingTimeType()?.editUnit !== DurationUnit.Minutes);
		}
	}

	protected updateCustomFields(recording: ReportWorkingTime, definitions: CustomFieldDefinition[]) {
		if (recording.workingTimeTypeCategory === WorkingTimeTypeCategory.AttendanceTime) {
			super.updateCustomFields(recording, definitions);
		}
	}

	private getSelectedWorkingTimeType(): WorkingTimeType | null {
		return this.getWorkingTimeTypeById(parseInt(this.dom.find("#workingTimeDialogWorkingTimeType").val() as string, 10));
	}

	private getWorkingTimeTypeById(id: number): WorkingTimeType | null {
		const foundWorkingTimeTypes = $.grep(this.config.workingTimeTypes, (workingTimeType: WorkingTimeType, _index: number) => {
			return workingTimeType.id === id;
		});
		return foundWorkingTimeTypes.length > 0 ? foundWorkingTimeTypes[0] : null;
	}

	protected updateBorder() {
		const modalContent = this.dom.parents("div.modal-content");

		modalContent.removeClass("border-NOT_INITIALIZED");

		Object.keys(WorkingTimeTypeCategory)
			.map(k => WorkingTimeTypeCategory[k as keyof typeof WorkingTimeTypeCategory])
			.forEach(c => {
				modalContent.removeClass("border-" + c);

			});

		if (this.getSelectedWorkingTimeType() !== null) {
			modalContent.addClass("border-" + this.getSelectedWorkingTimeType().category);
		}
	}

	protected filterUsers(users: User[]): User[] {
		/* For working time we always show all active users and cannot simply filter to dateTimeChangeable.
		For day-based working time types the recordingMode takes effect instead of dateTimeChangeable. */
		return $.grep(users, user => !user.disabled);
	}

	protected isDateTimeChangeAllowed(userId?: number): boolean {
		return (this.dialogMode === DialogMode.Create || this.editUnit !== DurationUnit.Days && this.editUnit !== DurationUnit.HalfDays) &&
			isWorkingTimeDateTimeChangeAllowed(false, userId || this.getSelectedUserId(), this.config.allowedUserIds);
	}

	protected isDeletable(recording: ReportWorkingTime): boolean {
		return isWorkingTimeDeletable(recording, this.isDateTimeChangeAllowed(recording.userId));
	}

	private isRequestCreation(): boolean {
		return (this.dialogMode === DialogMode.Create || this.dialogMode === DialogMode.Duplicate) &&
			SecurityStatus.isWorkingTimeRequestsEnabled() &&
			this.getSelectedWorkingTimeType()?.recordingModeUser === WorkingTimeTypeRecordingMode.BY_REQUEST;
	}

	protected configureDateTimeFields(enabled: boolean) {
		super.configureDateTimeFields(enabled || this.isRequestCreation() ||
			this.editUnit !== DurationUnit.Minutes && (this.dialogMode === DialogMode.Create || this.dialogMode === DialogMode.Duplicate));
	}

	protected updateWorkingTimeTypeSelect(workingTimeTypes: WorkingTimeType[]) {
		const workingTimeTypeSelect = this.dom.find("#workingTimeDialogWorkingTimeType");
		const old = workingTimeTypeSelect.val();

		DOMUtil.fillWorkingTimeTypeSelect(workingTimeTypeSelect, workingTimeTypes, false);

		if (old !== workingTimeTypeSelect.val()) {
			this.onSelectedWorkingTimeTypeChanged();
		}
	}

	private updateSubstituteValidationStartEndDate() {
		const startDateString = this.dom.find(".startDate").val() as string;
		const endDateString = this.dom.find(".endDate").val() as string;
		this.substituteValidation?.setStartEndDate(parseDateInput(startDateString), parseDateInput(endDateString));
	}

	private updateProofAttachmentGallery() {
		let proofRequirement = ProofRequirement.NOT_APPLICABLE;

		if (this.dialogMode !== DialogMode.Edit && this.dialogMode !== DialogMode.Show) {
			const workingTimeType = this.getSelectedWorkingTimeType();
			proofRequirement = workingTimeType?.proofRequirement ?? ProofRequirement.NOT_APPLICABLE;
		}

		this.proofAttachmentGallery?.update(proofRequirement);
	}

	protected getStatus(record: ReportWorkingTime): RecordedEntryStatus {
		return record.status;
	}
}
